import React, { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  CurrencyCircleDollar,
  Upload,
  FileXls,
  CheckCircle,
  Warning,
  Download,
  Trash,
  Table,
  Buildings,
  Phone,
  Envelope,
  ShieldPlus,
  Users,
  HardHat,
  Brain,
  Sparkle,
  TrendUp,
  IdentificationCard,
  Baby,
  UserCircle,
  Pulse,
  SealCheck,
  ShieldCheck,
  CircleNotch,
  WarningCircle
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Layout } from "../../components/layout/Layout";
import { formatCurrency, API_URL } from "../../lib/utils";
import { LiquidGlassBg } from "../../components/ui/LiquidGlassComponents";

const AGE_BANDS = ["0-5", "6-10", "11-18", "19-25", "26-35", "36-45", "46-50", "51-55", "56-58", "59-65", "66-75", "76+"];
const MATERNITY_AGE_BANDS = ["18-22", "23-34", "35-45"];
const RELATIONS = ["Employee", "Spouse", "Child", "Parent"];
const STEP_LABELS = ["Company", "Input", "Benefits", "Census", "Results"];

export default function InsuranceCalculator() {
  const [step, setStep] = useState(1);

  // Step 1
  const [leadInfo, setLeadInfo] = useState({ company_name: "", contact_number: "", company_email: "" });

  // Step 2
  const [inputMode, setInputMode] = useState(null);
  const inputModeRef = useRef(null);

  // Confirmed mode - set explicitly when entering Step 4
  const [confirmedMode, setConfirmedMode] = useState(null);

  // Step 3
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [planConfigs, setPlanConfigs] = useState({});

  // Step 4 — Excel
  const [censusFile, setCensusFile] = useState(null);
  const [censusData, setCensusData] = useState([]);
  const [censusSummary, setCensusSummary] = useState(null);
  const fileInputRef = useRef(null);

  // Step 4 — Manual
  const [manualData, setManualData] = useState({});
  const [manualMaternity, setManualMaternity] = useState({});
  const [activeManualPlan, setActiveManualPlan] = useState(null);

  // Step 5
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState("");

  // ---- Helpers ----
  const selectMode = (mode) => {
    inputModeRef.current = mode;
    setInputMode(mode);
    if (mode === "excel") {
      setManualData({});
      setManualMaternity({});
      setActiveManualPlan(null);
    } else {
      setCensusFile(null);
      setCensusData([]);
      setCensusSummary(null);
    }
  };

  const togglePlan = (plan) => {
    setSelectedPlans((prev) => {
      if (prev.includes(plan)) return prev.filter((p) => p !== plan);
      return [...prev, plan].sort();
    });
    if (!planConfigs[plan]) {
      setPlanConfigs((prev) => ({
        ...prev,
        [plan]: { hospital_coverage_limit: 500000, room_limit_amount: 15000, maternity_option: false, maternity_normal_limit: 50000, maternity_c_section_limit: 100000 },
      }));
    }
  };

  const updatePlanConfig = (plan, field, value) => {
    setPlanConfigs((prev) => ({ ...prev, [plan]: { ...prev[plan], [field]: value } }));
  };

  const initManualData = () => {
    const data = {};
    const mat = {};
    for (const plan of selectedPlans) {
      if (!manualData[plan]) {
        data[plan] = {};
        for (const rel of RELATIONS) {
          data[plan][rel] = {};
          for (const band of AGE_BANDS) data[plan][rel][band] = 0;
        }
      } else {
        data[plan] = manualData[plan];
      }
      mat[plan] = manualMaternity[plan] || {};
      for (const band of MATERNITY_AGE_BANDS) {
        mat[plan][band] = mat[plan][band] || { employees: 0, spouses: 0 };
      }
    }
    setManualData(data);
    setManualMaternity(mat);
    setActiveManualPlan(selectedPlans[0] || null);
  };

  const updateManualCount = (plan, relation, ageBand, value) => {
    const v = Math.max(0, parseInt(value) || 0);
    setManualData((prev) => ({
      ...prev,
      [plan]: { ...prev[plan], [relation]: { ...prev[plan]?.[relation], [ageBand]: v } },
    }));
  };

  const updateMaternityCount = (plan, ageBand, field, value) => {
    const v = Math.max(0, parseInt(value) || 0);
    setManualMaternity((prev) => ({
      ...prev,
      [plan]: { ...prev[plan], [ageBand]: { ...prev[plan]?.[ageBand], [field]: v } },
    }));
  };

  const manualTotals = useMemo(() => {
    const totals = {};
    for (const plan of selectedPlans) {
      let planTotal = 0;
      const relTotals = {};
      for (const rel of RELATIONS) {
        let t = 0;
        for (const band of AGE_BANDS) t += manualData[plan]?.[rel]?.[band] || 0;
        relTotals[rel] = t;
        planTotal += t;
      }
      totals[plan] = { ...relTotals, total: planTotal };
    }
    return totals;
  }, [manualData, selectedPlans]);

  const totalManualLives = useMemo(() => {
    return Object.values(manualTotals).reduce((sum, p) => sum + p.total, 0);
  }, [manualTotals]);

  // ---- File Upload ----
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    setError("");
    setCensusFile(file);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await fetch(`${API_URL}/calculators/parse-census`, { method: "POST", body: formData });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to parse file");
      }
      const data = await response.json();
      setCensusData(data.census_data);
      setCensusSummary(data.summary);
    } catch (err) {
      setError(err.message);
      setCensusFile(null);
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/calculators/census-template`);
      if (!response.ok) throw new Error("Template not found on server");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "census_template.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError("Failed to download template. Please ensure the backend server is running and the template endpoint is reachable.");
    }
  };


  const handleSubmit = async () => {
    const publicDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com"];
    const emailDomain = leadInfo.company_email.split("@")[1]?.toLowerCase();
    if (publicDomains.includes(emailDomain)) {
      setError("Please enter your official company email address to view your premium estimate.");
      return;
    }

    setLoading(true);
    setError("");

    const activeMode = confirmedMode || inputModeRef.current || inputMode;
    const benefit_structures = selectedPlans.map((plan) => ({ plan, ...planConfigs[plan] }));
    const body = { lead_info: leadInfo, benefit_structures, input_mode: activeMode };

    if (activeMode === "excel") {
      body.census_data = censusData.length > 0 ? censusData : null;
    } else {
      const manual_population = [];
      const manual_maternity_arr = [];
      for (const plan of selectedPlans) {
        for (const rel of RELATIONS) {
          for (const band of AGE_BANDS) {
            const count = manualData[plan]?.[rel]?.[band] || 0;
            if (count > 0) manual_population.push({ plan, relation: rel, age_band: band, count });
          }
        }
        if (planConfigs[plan]?.maternity_option) {
          for (const band of MATERNITY_AGE_BANDS) {
            const emp = manualMaternity[plan]?.[band]?.employees || 0;
            const sp = manualMaternity[plan]?.[band]?.spouses || 0;
            if (emp + sp > 0) manual_maternity_arr.push({ plan, maternity_age_band: band, female_count: emp + sp });
          }
        }
      }
      body.manual_population = manual_population;
      body.manual_maternity = manual_maternity_arr;
    }

    try {
      const response = await fetch(`${API_URL}/calculators/insurance-premium`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Calculation failed");
      }
      const data = await response.json();
      setResult(data);
      setStep(5);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const step1Valid = leadInfo.company_name && leadInfo.contact_number && leadInfo.company_email;
  const step2Valid = inputMode !== null;
  const step3Valid = selectedPlans.length > 0 && selectedPlans.every((p) => planConfigs[p]?.hospital_coverage_limit >= 50000 && planConfigs[p]?.room_limit_amount >= 1000);
  const step4Valid = (confirmedMode || inputMode) === "excel" ? censusData.length > 0 : totalManualLives > 0;

  return (
    <Layout>
      <section className="bg-[#030712] min-h-screen relative overflow-hidden pt-32 pb-20">
        <LiquidGlassBg />

        <div className="container-wide relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#0048ff] text-sm font-medium mb-6 backdrop-blur-md">
                <ShieldPlus size={18} weight="bold" />
                <span>Premium Estimate Engine</span>
              </div>
              <h1 className="text-[40px] md:text-[64px] font-bold text-white mb-6 tracking-tighter leading-tight">
                Policy <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0048ff] to-cyan-400">Premium</span> Estimator
              </h1>
              <p className="text-white/40 text-lg max-w-2xl mx-auto leading-relaxed">
                Architect your group's benefits by uploading a census or manual matrix to receive high-governance premium projections.
              </p>
            </motion.div>

            {/* Stepper */}
            <div className="flex justify-center mb-16 px-4">
              <div className="flex items-center w-full max-w-4xl">
                {STEP_LABELS.map((label, i) => {
                  const s = i + 1;
                  const StepIcon = [Buildings, Table, HardHat, Users, Brain][i];
                  return (
                    <React.Fragment key={s}>
                      <div className="flex flex-col items-center relative group">
                        <div
                          className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 border-2 ${
                            step >= s
                              ? "bg-[#0048ff] border-[#0048ff] text-white shadow-[0_0_30px_rgba(0,72,255,0.4)]"
                              : "bg-white/5 border-white/10 text-white/20"
                          }`}
                        >
                          {step > s ? <CheckCircle size={28} weight="fill" /> : <StepIcon size={24} weight="duotone" />}
                        </div>
                        <span className={`text-[10px] uppercase font-bold tracking-[0.2em] mt-3 hidden sm:block ${step >= s ? "text-[#0048ff]" : "text-white/20"}`}>
                          {label}
                        </span>
                      </div>
                      {s < 5 && (
                        <div className="flex-1 h-px mx-4 mt-[-24px]">
                          <div className={`h-full transition-all duration-1000 ${step > s ? "bg-[#0048ff]" : "bg-white/10"}`} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <AnimatePresence mode="wait">

              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="glass-panel p-8 md:p-12 border-white/10"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                      <h2 className="text-3xl font-bold text-white mb-6">Organization <span className="text-[#0048ff]">Profile</span></h2>
                      <p className="text-white/40 mb-8 leading-relaxed">
                        To generate a high-precision premium estimate, we require your corporate credentials. This ensures the projection is tailored to your specific industry risk profile.
                      </p>
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <Label className="text-white/60 font-medium">Company Name</Label>
                          <div className="relative group">
                            <Buildings className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#0048ff] transition-colors" size={20} />
                            <Input
                              className="bg-white/5 border-white/10 text-white pl-12 h-14 focus:border-[#0048ff] transition-all"
                              placeholder="e.g. Acme Corporation"
                              value={leadInfo.company_name}
                              onChange={(e) => setLeadInfo({ ...leadInfo, company_name: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/60 font-medium">Contact Number</Label>
                          <div className="relative group">
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#0048ff] transition-colors" size={20} />
                            <Input
                              className="bg-white/5 border-white/10 text-white pl-12 h-14 focus:border-[#0048ff] transition-all"
                              placeholder="+92 300 1234567"
                              value={leadInfo.contact_number}
                              onChange={(e) => setLeadInfo({ ...leadInfo, contact_number: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/60 font-medium">Official Email Address</Label>
                          <div className="relative group">
                            <Envelope className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#0048ff] transition-colors" size={20} />
                            <Input
                              className="bg-white/5 border-white/10 text-white pl-12 h-14 focus:border-[#0048ff] transition-all"
                              placeholder="name@company.com"
                              value={leadInfo.company_email}
                              onChange={(e) => setLeadInfo({ ...leadInfo, company_email: e.target.value })}
                            />
                          </div>
                        </div>
                        {error && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                            <Warning size={18} weight="fill" />
                            <span>{error}</span>
                          </motion.div>
                        )}
                        <Button
                          className="w-full h-14 bg-[#0048ff] hover:bg-[#0036cc] text-white rounded-xl font-bold text-lg shadow-[0_10px_30px_rgba(0,72,255,0.3)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                          disabled={!step1Valid}
                          onClick={() => setStep(2)}
                        >
                          Initialize Analysis
                          <ArrowRight size={20} weight="bold" />
                        </Button>
                      </div>
                    </div>
                    <div className="hidden md:block relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0048ff]/20 to-transparent rounded-3xl blur-3xl" />
                      <div className="relative glass-panel p-8 border-white/10 transform hover:rotate-0 transition-all duration-700">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-12 h-12 rounded-full bg-[#0048ff]/20 flex items-center justify-center">
                            <ShieldPlus size={24} className="text-[#0048ff]" />
                          </div>
                          <div>
                            <h4 className="text-white font-bold">Secure Verification</h4>
                            <p className="text-white/40 text-xs">Standard Governance Protocol</p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="h-2 bg-white/5 rounded-full overflow-hidden">
                              <motion.div
                                className="h-full bg-[#0048ff]"
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.random() * 100}%` }}
                                transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-white mb-4">Input <span className="text-[#0048ff]">Methodology</span></h2>
                    <p className="text-white/40">Select the data ingestion protocol for your employee census.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <motion.div
                      whileHover={{ scale: 1.02, translateY: -5 }}
                      onClick={() => selectMode("excel")}
                      className={`relative group cursor-pointer p-8 rounded-[32px] border-2 transition-all duration-500 ${
                        inputMode === "excel" ? "bg-[#0048ff]/10 border-[#0048ff] shadow-[0_20px_50px_rgba(0,72,255,0.2)]" : "bg-white/5 border-white/10 hover:border-white/20"
                      }`}
                    >
                      <div className="flex flex-col h-full">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${
                          inputMode === "excel" ? "bg-[#0048ff] text-white" : "bg-white/5 text-white/20 group-hover:text-white"
                        }`}>
                          <FileXls size={32} weight="duotone" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-3 tracking-tight">Bulk Excel Upload</h3>
                        <p className="text-white/40 text-sm leading-relaxed mb-8">
                          Ingest large-scale census data using our standardized template. Recommended for teams exceeding 50 lives.
                        </p>
                        <div className="mt-auto flex items-center gap-2 text-[#0048ff] font-bold text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all">
                          Select Protocol <ArrowRight size={16} weight="bold" />
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      whileHover={{ scale: 1.02, translateY: -5 }}
                      onClick={() => selectMode("manual")}
                      className={`relative group cursor-pointer p-8 rounded-[32px] border-2 transition-all duration-500 ${
                        inputMode === "manual" ? "bg-[#0048ff]/10 border-[#0048ff] shadow-[0_20px_50px_rgba(0,72,255,0.2)]" : "bg-white/5 border-white/10 hover:border-white/20"
                      }`}
                    >
                      <div className="flex flex-col h-full">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${
                          inputMode === "manual" ? "bg-[#0048ff] text-white" : "bg-white/5 text-white/20 group-hover:text-white"
                        }`}>
                          <Table size={32} weight="duotone" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-3 tracking-tight">Interactive Matrix</h3>
                        <p className="text-white/40 text-sm leading-relaxed mb-8">
                          Configure a custom distribution matrix manually. Optimal for rapid estimates or smaller teams.
                        </p>
                        <div className="mt-auto flex items-center gap-2 text-[#0048ff] font-bold text-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all">
                          Select Protocol <ArrowRight size={16} weight="bold" />
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  <div className="flex items-center justify-center gap-4 mt-12">
                    <Button
                      className="px-8 h-14 bg-white/5 hover:bg-white/10 text-white rounded-xl font-bold transition-all border border-white/10"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      className="px-8 h-14 bg-[#0048ff] hover:bg-[#0036cc] text-white rounded-xl font-bold transition-all shadow-lg disabled:opacity-50"
                      disabled={!step2Valid}
                      onClick={() => { setError(""); setStep(3); }}
                    >
                      Next Step
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-white mb-4">Coverage <span className="text-[#0048ff]">Architecture</span></h2>
                    <p className="text-white/40">Define the benefit tiers and limits for your group policy.</p>
                  </div>

                  <div className="glass-panel p-8 md:p-12 border-white/10">
                    <div className="space-y-12">
                      <div>
                        <Label className="text-white/60 font-medium block mb-6">Select Tiers</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {["A", "B", "C", "D"].map((plan) => (
                            <div
                              key={plan}
                              onClick={() => togglePlan(plan)}
                              className={`p-6 rounded-2xl border-2 cursor-pointer text-center transition-all duration-300 ${
                                selectedPlans.includes(plan)
                                  ? "bg-[#0048ff]/20 border-[#0048ff] shadow-[0_0_20px_rgba(0,72,255,0.2)]"
                                  : "bg-white/5 border-white/10 hover:border-white/20"
                              }`}
                            >
                              <span className={`text-2xl font-black ${selectedPlans.includes(plan) ? "text-white" : "text-white/20"}`}>Tier {plan}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-8">
                        {selectedPlans.map((plan) => (
                          <motion.div
                            key={plan}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-8 rounded-3xl bg-white/5 border border-white/10 relative overflow-hidden group"
                          >
                            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                              <HardHat size={64} weight="duotone" className="text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-8 flex items-center gap-3">
                              <span className="w-8 h-8 rounded-lg bg-[#0048ff] flex items-center justify-center text-xs">Tier {plan}</span>
                              Configuration
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                              <div className="space-y-2">
                                <Label className="text-white/40 text-xs font-bold uppercase tracking-widest">Hospital Coverage Limit (PKR)</Label>
                                <Input
                                  type="number"
                                  className="bg-white/5 border-white/10 text-white h-12 focus:border-[#0048ff]"
                                  value={planConfigs[plan]?.hospital_coverage_limit}
                                  onChange={(e) => updatePlanConfig(plan, "hospital_coverage_limit", parseInt(e.target.value) || 0)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-white/40 text-xs font-bold uppercase tracking-widest">Room Limit / Day (PKR)</Label>
                                <Input
                                  type="number"
                                  className="bg-white/5 border-white/10 text-white h-12 focus:border-[#0048ff]"
                                  value={planConfigs[plan]?.room_limit_amount}
                                  onChange={(e) => updatePlanConfig(plan, "room_limit_amount", parseInt(e.target.value) || 0)}
                                />
                              </div>
                            </div>

                            <div className="mt-8 pt-8 border-t border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-6">
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${planConfigs[plan]?.maternity_option ? "bg-[#0048ff] text-white" : "bg-white/5 text-white/20"}`}>
                                  <Users size={24} weight="duotone" />
                                </div>
                                <div>
                                  <p className="text-white font-bold">Maternity Rider</p>
                                  <p className="text-white/40 text-xs whitespace-nowrap">Include wellness</p>
                                </div>
                                <Switch
                                  checked={planConfigs[plan]?.maternity_option}
                                  onCheckedChange={(v) => updatePlanConfig(plan, "maternity_option", v)}
                                />
                              </div>

                              {planConfigs[plan]?.maternity_option && (
                                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex gap-4">
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-white/30 uppercase font-bold tracking-tight">Normal</span>
                                    <Input
                                      className="bg-white/5 border-white/10 text-white h-10 w-24 text-xs"
                                      value={planConfigs[plan]?.maternity_normal_limit}
                                      onChange={(e) => updatePlanConfig(plan, "maternity_normal_limit", parseInt(e.target.value) || 0)}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <span className="text-[10px] text-white/30 uppercase font-bold tracking-tight">C-Section</span>
                                    <Input
                                      className="bg-white/5 border-white/10 text-white h-10 w-24 text-xs"
                                      value={planConfigs[plan]?.maternity_c_section_limit}
                                      onChange={(e) => updatePlanConfig(plan, "maternity_c_section_limit", parseInt(e.target.value) || 0)}
                                    />
                                  </div>
                                </motion.div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-4 mt-12">
                    <Button
                      className="px-8 h-14 bg-white/5 hover:bg-white/10 text-white rounded-xl font-bold transition-all border border-white/10"
                      onClick={() => setStep(2)}
                    >
                      Back
                    </Button>
                    <Button
                      className="px-8 h-14 bg-[#0048ff] hover:bg-[#0036cc] text-white rounded-xl font-bold transition-all shadow-lg disabled:opacity-50"
                      disabled={!step3Valid}
                      onClick={() => {
                        setError("");
                        const mode = inputModeRef.current || inputMode;
                        setConfirmedMode(mode);
                        if (mode === "manual") initManualData();
                        setStep(4);
                      }}
                    >
                      Configure Census
                    </Button>
                  </div>
                </motion.div>
              )}

              {step === 4 && !loading && (
                <motion.div
                  key={`step4-${confirmedMode}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-white mb-4">
                      {confirmedMode === "excel" ? "Census <span class='text-[#0048ff]'>Ingestion</span>" : "Population <span class='text-[#0048ff]'>Matrix</span>"}
                    </h2>
                    <p className="text-white/40">
                      {confirmedMode === "excel" 
                        ? "Upload your employee spreadsheet for automated processing." 
                        : "Define headcount distribution across age bands and relation types."}
                    </p>
                  </div>

                  <div className="glass-panel p-8 md:p-12 border-white/10">
                    {/* EXCEL MODE */}
                    {confirmedMode === "excel" && (
                      <div className="space-y-8">
                        <div 
                          onClick={() => !uploadLoading && fileInputRef.current?.click()}
                          className={`group relative border-2 border-dashed rounded-[32px] p-12 text-center transition-all duration-500 cursor-pointer ${
                            censusFile 
                              ? "border-emerald-500/50 bg-emerald-500/5" 
                              : "border-white/10 bg-white/5 hover:border-[#0048ff]/50 hover:bg-[#0048ff]/5"
                          }`}
                        >
                          <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".xlsx,.xls" className="hidden" />
                          
                          {uploadLoading ? (
                            <div className="flex flex-col items-center py-4">
                              <div className="w-12 h-12 rounded-full border-2 border-[#0048ff] border-t-transparent animate-spin mb-4" />
                              <p className="text-white font-medium">Analyzing Data Structures...</p>
                            </div>
                          ) : censusFile ? (
                            <div className="flex flex-col items-center">
                              <div className="w-20 h-20 rounded-2xl bg-emerald-500/20 text-emerald-500 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
                                <FileXls size={40} weight="duotone" />
                              </div>
                              <h4 className="text-white text-xl font-bold mb-2">{censusFile.name}</h4>
                              <p className="text-emerald-500/60 text-sm mb-6">File successfully verified</p>
                              <Button 
                                variant="outline" 
                                className="border-white/10 text-white/60 hover:text-red-500 hover:border-red-500/50 h-10 px-6 rounded-xl transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCensusFile(null);
                                  setCensusData([]);
                                  setCensusSummary(null);
                                  if (fileInputRef.current) fileInputRef.current.value = "";
                                }}
                              >
                                <Trash size={16} className="mr-2" /> Remove File
                              </Button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="w-20 h-20 rounded-2xl bg-white/5 text-white/20 group-hover:text-[#0048ff] group-hover:bg-[#0048ff]/10 flex items-center justify-center mb-6 transition-all duration-500">
                                <Upload size={40} weight="duotone" />
                              </div>
                              <h4 className="text-white text-xl font-bold mb-2">Drop Census Data</h4>
                              <p className="text-white/40 text-sm mb-8 max-w-xs mx-auto">Upload .xlsx or .xls files for direct system ingestion.</p>
                              <div className="px-8 h-12 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-all flex items-center justify-center">
                                Browse Files
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between p-6 rounded-2xl bg-white/5 border border-white/5">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-[#0048ff]/20 text-[#0048ff] flex items-center justify-center">
                              <Download size={20} weight="bold" />
                            </div>
                            <div>
                              <p className="text-white font-bold text-sm">Download Protocol</p>
                              <p className="text-white/40 text-xs">Standard CSV/XLS Template</p>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            className="text-[#0048ff] hover:bg-[#0048ff]/10 h-10 rounded-lg font-bold"
                            onClick={downloadTemplate}
                          >
                            Template.xlsx
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* MANUAL MODE */}
                    {confirmedMode === "manual" && (
                      <div className="space-y-12">
                        <div className="flex flex-wrap gap-3 p-1 bg-white/5 rounded-2xl border border-white/5">
                          {selectedPlans.map((plan) => (
                            <button
                              key={plan}
                              onClick={() => setActiveManualPlan(plan)}
                              className={`flex-1 min-w-[120px] px-6 py-3 rounded-xl font-bold text-sm transition-all duration-300 ${
                                activeManualPlan === plan
                                  ? "bg-[#0048ff] text-white shadow-lg"
                                  : "text-white/40 hover:text-white hover:bg-white/5"
                              }`}
                            >
                              Tier {plan}
                              {manualTotals[plan]?.total > 0 && (
                                <span className="ml-2 py-0.5 px-2 bg-black/20 rounded-md text-[10px]">{manualTotals[plan].total}</span>
                              )}
                            </button>
                          ))}
                        </div>

                        {activeManualPlan && (
                          <motion.div
                            key={activeManualPlan}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-10"
                          >
                            <div className="overflow-hidden rounded-[32px] border border-white/10 bg-white/5 backdrop-blur-md">
                              <table className="w-full text-left overflow-hidden">
                                <thead>
                                  <tr className="border-b border-white/10 bg-white/5">
                                    <th className="px-8 py-6 text-xs font-bold uppercase tracking-widest text-white/40">Age Spectrum</th>
                                    {RELATIONS.map((rel) => (
                                      <th key={rel} className="px-8 py-6 text-xs font-bold uppercase tracking-widest text-white/40 text-center">
                                        {rel}s
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {AGE_BANDS.map((band) => (
                                    <tr key={band} className="hover:bg-white/5 transition-colors group">
                                      <td className="px-8 py-4 font-bold text-white group-hover:text-[#0048ff] transition-colors">{band}</td>
                                      {RELATIONS.map((rel) => (
                                        <td key={rel} className="px-8 py-4 text-center">
                                          <input
                                            type="number"
                                            value={manualData[activeManualPlan]?.[rel]?.[band] || 0}
                                            onChange={(e) => updateManualCount(activeManualPlan, rel, band, e.target.value)}
                                            className="w-20 h-10 bg-transparent border border-white/10 rounded-xl text-center text-white font-bold focus:border-[#0048ff] focus:ring-1 focus:ring-[#0048ff]/30 outline-none transition-all"
                                          />
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                  <tr className="bg-[#0048ff]/10">
                                    <td className="px-8 py-6 font-black text-[#0048ff] uppercase tracking-tighter text-xl">Total Count</td>
                                    {RELATIONS.map((rel) => (
                                      <td key={rel} className="px-8 py-6 text-center text-xl font-black text-white">
                                        {manualTotals[activeManualPlan]?.[rel] || 0}
                                      </td>
                                    ))}
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {planConfigs[activeManualPlan]?.maternity_option && (
                              <div className="p-8 rounded-[32px] bg-[#0048ff]/5 border border-[#0048ff]/20">
                                <h4 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                                  <Baby size={28} weight="duotone" className="text-[#0048ff]" />
                                  Maternity Census (18-45 Spectrum)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                  {MATERNITY_AGE_BANDS.map((band) => (
                                    <div key={band} className="p-6 rounded-2xl bg-white/5 border border-white/5">
                                      <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-4">Bracket: {band}</p>
                                      <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                          <Label className="text-[10px] text-white/30 uppercase font-bold">Employees</Label>
                                          <Input
                                            type="number"
                                            className="bg-white/5 border-white/10 text-white h-10 font-bold"
                                            value={manualMaternity[activeManualPlan]?.[band]?.employees || 0}
                                            onChange={(e) => updateMaternityCount(activeManualPlan, band, "employees", e.target.value)}
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          <Label className="text-[10px] text-white/30 uppercase font-bold">Spouses</Label>
                                          <Input
                                            type="number"
                                            className="bg-white/5 border-white/10 text-white h-10 font-bold"
                                            value={manualMaternity[activeManualPlan]?.[band]?.spouses || 0}
                                            onChange={(e) => updateMaternityCount(activeManualPlan, band, "spouses", e.target.value)}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-4 mt-12">
                    <Button
                      className="px-8 h-14 bg-white/5 hover:bg-white/10 text-white rounded-xl font-bold transition-all border border-white/10"
                      onClick={() => setStep(3)}
                    >
                      Back
                    </Button>
                    <Button
                      className="px-8 h-14 bg-[#0048ff] hover:bg-[#0036cc] text-white rounded-xl font-bold transition-all shadow-lg disabled:opacity-50 min-w-[200px]"
                      disabled={!step4Valid || loading}
                      onClick={handleSubmit}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <CircleNotch size={20} className="animate-spin" /> Calculating...
                        </span>
                      ) : (
                        "Generate Final Report"
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}

            {/* ===== Loading State ===== */}
            {loading && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-24">
                <div className="w-16 h-16 mx-auto mb-6 relative">
                  <div className="absolute inset-0 rounded-full border-[3px] border-[#0048ff]/20" />
                  <div className="absolute inset-0 rounded-full border-[3px] border-[#0048ff] border-t-transparent animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-[#11111f] mb-2">Calculating Your Premium</h3>
                <p className="text-[#11111f]/50">Processing census data and applying rate matrices...</p>
              </motion.div>
            )}

              {step === 5 && result && !loading && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", damping: 20 }}
                  className="space-y-10"
                >
                  <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-bold uppercase tracking-widest mb-6">
                      <ShieldCheck size={16} weight="bold" /> Verification Secured
                    </div>
                    <h2 className="text-4xl font-black text-white mb-4">Prime Assessment</h2>
                    <p className="text-white/40">Consolidated insurance architecture based on provided parameters.</p>
                  </div>

                  <div className="glass-panel p-1 md:p-1 border-white/10 overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0048ff]/10 to-transparent pointer-events-none" />
                    
                    <div className="p-8 md:p-16 text-center relative border-b border-white/10">
                      <p className="text-[#0048ff] font-black uppercase tracking-[0.2em] text-sm mb-6">Estimated Annual Premium</p>
                      <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter mb-8 drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]">
                        {formatCurrency(result.estimated_annual_premium)}
                      </h1>
                      <div className="flex flex-wrap justify-center gap-8 py-8 border-y border-white/5">
                        <div className="space-y-1">
                          <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest">Base Premium</p>
                          <p className="text-white text-xl font-bold">{formatCurrency(result.breakdown.base_premium)}</p>
                        </div>
                        <div className="w-px h-12 bg-white/5 mx-2" />
                        <div className="space-y-1">
                          <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest">Maternity Rider</p>
                          <p className="text-white text-xl font-bold">{formatCurrency(result.breakdown.maternity_premium)}</p>
                        </div>
                        <div className="w-px h-12 bg-white/5 mx-2" />
                        <div className="space-y-1">
                          <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest">Per Life Average</p>
                          <p className="text-white text-xl font-bold">{formatCurrency(result.per_life_cost)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 divide-x divide-y md:divide-y-0 divide-white/10 border-b border-white/10 bg-white/5">
                      {[
                        { label: "Total Employees", val: result.census_summary.employees, icon: Users },
                        { label: "Spouses", val: result.census_summary.spouses, icon: IdentificationCard },
                        { label: "Children", val: result.census_summary.children, icon: Baby },
                        { label: "Parents", val: result.census_summary.parents, icon: UserCircle },
                        { label: "Net Lives", val: result.census_summary.total, icon: Pulse, active: true },
                      ].map((stat, i) => (
                        <div key={i} className={`p-8 flex flex-col items-center justify-center transition-all ${stat.active ? "bg-[#0048ff]/10" : "hover:bg-white/5"}`}>
                          <stat.icon size={24} weight="duotone" className={stat.active ? "text-[#0048ff] mb-3" : "text-white/20 mb-3"} />
                          <p className="text-2xl font-black text-white mb-1">{stat.val}</p>
                          <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest text-center">{stat.label}</p>
                        </div>
                      ))}
                    </div>

                    <div className="p-8 md:p-12 space-y-12">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {Object.entries(result.plan_breakdown).map(([plan, data]) => (
                          data.lives > 0 && (
                            <div key={plan} className="p-6 rounded-[24px] bg-white/5 border border-white/10 relative group hover:border-[#0048ff]/50 transition-all">
                              <p className="text-[#0048ff] text-[10px] font-black uppercase tracking-widest mb-4">Tier {plan}</p>
                              <p className="text-white text-2xl font-black mb-2">{formatCurrency(data.premium)}</p>
                              <p className="text-white/40 text-xs font-medium">{data.lives} Active Lives</p>
                              <div className="absolute top-6 right-6 opacity-5 group-hover:opacity-10 transition-opacity">
                                <SealCheck size={40} weight="duotone" className="text-white" />
                              </div>
                            </div>
                          )
                        ))}
                      </div>

                      <div className="p-6 rounded-2xl bg-amber-500/5 border border-amber-500/20 text-amber-500/80 text-xs leading-relaxed italic">
                        <span className="font-bold flex items-center gap-2 mb-2 uppercase tracking-[0.2em] text-[10px]">
                          <WarningCircle size={14} weight="bold" /> Actuarial Disclaimer
                        </span>
                        {result.disclaimer}
                      </div>

                      <div className="flex flex-wrap items-center justify-center gap-6 pt-8 border-t border-white/5">
                        <Link to="/register">
                          <Button className="h-14 px-10 bg-[#0048ff] hover:bg-[#0036cc] text-white rounded-xl font-bold transition-all shadow-lg group">
                            Register Organization <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                        <Button 
                          variant="outline" 
                          className="h-14 px-10 border-white/10 bg-white/5 hover:bg-white/10 text-white rounded-xl font-bold transition-all"
                          onClick={() => window.open('mailto:corporatebusiness@thementorhealth.com?subject=Formal Quote Request', '_blank')}
                        >
                          Request Formal Quotation
                        </Button>
                        <Button 
                          variant="ghost" 
                          className="h-14 px-10 text-white/40 hover:text-white transition-colors font-bold"
                          onClick={() => { setStep(1); setResult(null); setError(''); setConfirmedMode(null); setInputMode(null); inputModeRef.current = null; }}
                        >
                          Reset Parameters
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </div>
      </section>
    </Layout>
  );
}
